/*
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2025 (original work) Open Assessment Technologies SA
 *
 */

import { Userpilot } from 'userpilot';
import env from '@/config/env';
import { __ } from '@oat-sa-private/ui-core';
import * as userConfigurationService from './userConfigurationService';

const PROCESS_ENV = env('NODE_ENV');
const GA_TAG = env('GA_TAG');

let isUserPilotInitialized = false;

/**
 * Initialize the userpilot service
 * @param {object} user
 */
async function initializeUserPilot(user) {
    const userConfiguration = userConfigurationService.getConfig();

    const userPilotToken = userConfiguration?.userpilot?.token || env('USERPILOT_TOKEN');
    if (userPilotToken) {
        try {
            Userpilot.initialize(userPilotToken);
            const userId = user?.tenantId && user?.sub ? `${user.tenantId}|${user.sub}` : null;
            if (!userId) {
                return;
            }
            Userpilot.identify(userId, {
                name: user?.name,
                login: user?.sub,
                email: user?.email,
                roles: user?.roles,
                interfaceLanguage: __.getLocale(),
                company: {
                    id: user?.tenantId
                }
            });
            /* eslint-disable no-empty */

            isUserPilotInitialized = true;
        } catch (e) {}
    }
}

/**
 * Inserts the Google Analytics script into the document head
 */
function insertGAScript() {
    const scriptTag = document.createElement('script');
    scriptTag.async = true;
    scriptTag.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TAG}`;
    document.head.appendChild(scriptTag);

    const inlineScript = document.createElement('script');
    inlineScript.text = `
        window.dataLayer = window.dataLayer || [];
        function gtag() {
            dataLayer.push(arguments);
        }
        gtag('js', new Date());
        gtag('config', '${GA_TAG}', {
            environment: '${PROCESS_ENV === 'production' ? 'Production' : 'Internal'}'
        });
    `;
    document.head.appendChild(inlineScript);
}

/**
 * Informs the analytics service that the URL has changed
 */
export function urlChange() {
    Userpilot.reload();
}

/**
 * Informs the analytics service that the user has logged out
 */
export function logout() {
    Userpilot.destroy();
}

/**
 * Initialize the analytics service
 * @param {object} user
 */
export async function initialize(user) {
    if (!isUserPilotInitialized) {
        await initializeUserPilot(user);
    }
    insertGAScript();
}
